import type { Screen, Section, Tone } from "./model.js";

/**
 * Screen builders, one per tool domain.
 *
 * Every builder is pure: already-extracted data in, a `Screen` out. No fetching,
 * no unwrapping of API envelopes. That division is the point — `fleet_health_app`
 * broke precisely because it re-implemented extraction that a working tool
 * already did, and drifted. Callers pass the output of the proven extractors
 * (`extractEsimStatusCounts`, `extractUsageTimeline`, …); builders only shape it.
 */

const pct = (part: number, total: number): string =>
  total > 0 ? `${((part / total) * 100).toFixed(1)}%` : "0%";

const money = (value: number, currency = ""): string =>
  `${currency}${value.toFixed(2)}`.trim();

/** Bytes → human units. Fleet data arrives in bytes and MB inconsistently. */
export function humanBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  const gb = bytes / 1_073_741_824;
  if (gb >= 1) return `${gb.toFixed(gb >= 10 ? 0 : 1)} GB`;
  return `${(bytes / 1_048_576).toFixed(0)} MB`;
}

// ── Fleet ────────────────────────────────────────────────────────────────────

export interface FleetAccount {
  name: string;
  balance: number;
  active: number;
  suspended: number;
  inventory: number;
  other: number;
  packageOnly?: boolean;
}

export interface FleetInput {
  active: number;
  suspended: number;
  inventory: number;
  other: number;
  accounts: FleetAccount[];
  /** Set when an underlying call failed, so the screen can say so. */
  unavailable?: string[];
}

export function fleetScreen(input: FleetInput): Screen {
  const total = input.active + input.suspended + input.inventory + input.other;
  const lowBalance = input.accounts.filter((a) => a.balance < 10);
  const criticals = lowBalance.filter((a) => a.packageOnly && a.balance === 0);

  const sections: Section[] = [
    {
      kind: "metrics",
      items: [
        { label: "Total eSIMs", value: total },
        { label: "Active", value: input.active, hint: pct(input.active, total), tone: "ok" },
        { label: "Inventory", value: input.inventory, hint: pct(input.inventory, total) },
        {
          label: "Suspended",
          value: input.suspended,
          hint: pct(input.suspended, total),
          tone: input.suspended > input.active * 0.1 ? "warn" : undefined,
        },
        { label: "Accounts", value: input.accounts.length },
        {
          label: "Low balance",
          value: lowBalance.length,
          tone: lowBalance.length > 0 ? "warn" : "ok",
        },
      ],
    },
  ];

  // Only chart the per-account split when it is actually populated. The
  // account list call carries balances and flags but no status breakdown, so
  // drawing this from it yields a row of empty bars that looks like a fleet
  // with nothing in it — a chart that misinforms is worse than no chart.
  const hasPerAccountCounts = input.accounts.some(
    (a) => a.active + a.suspended + a.inventory + a.other > 0,
  );
  if (hasPerAccountCounts) {
    sections.push({
      kind: "bars",
      title: "eSIMs by account",
      items: [...input.accounts]
        .sort((a, b) => b.active + b.inventory - (a.active + a.inventory))
        .slice(0, 10)
        .map((a) => ({
          label: a.name,
          value: a.active + a.suspended + a.inventory + a.other,
          hint: `${a.active} active`,
          tone: a.active > 0 ? ("ok" as Tone) : ("muted" as Tone),
        })),
    });
  }

  sections.push({
    kind: "table",
    title: "Accounts",
    columns: ["Account", "Balance", "Active", "Inventory"],
    numeric: [1, 2, 3],
    empty: "No accounts under this reseller.",
    rows: input.accounts.map((a) => [a.name, money(a.balance), a.active, a.inventory]),
  });

  if (criticals.length > 0) {
    sections.push({
      kind: "note",
      tone: "critical",
      text: `${criticals.length} package-only account(s) at zero balance — packages cannot be assigned until topped up: ${criticals
        .map((a) => a.name)
        .join(", ")}.`,
    });
  }
  for (const missing of input.unavailable ?? []) {
    sections.push({ kind: "note", tone: "warn", text: `Unavailable: ${missing}` });
  }

  return {
    id: "fleet-health",
    title: "Fleet health",
    subtitle: `${total} eSIMs across ${input.accounts.length} account(s) · ${pct(input.active, total)} utilisation`,
    sections,
    actions: [
      { label: "Top up an account", command: "wallet_topup_checkout" },
      { label: "Per-account eSIM status", command: "esim_status_per_account" },
    ],
  };
}

// ── Subscribers ──────────────────────────────────────────────────────────────

export interface SubscriberRow {
  iccid: string;
  msisdn?: string;
  status: string;
  account?: string;
  dataUsedBytes?: number;
}

export function subscribersScreen(rows: SubscriberRow[], opts: { account?: string } = {}): Screen {
  const byStatus = new Map<string, number>();
  for (const r of rows) byStatus.set(r.status, (byStatus.get(r.status) ?? 0) + 1);

  return {
    id: "subscribers",
    title: "Subscribers",
    subtitle: opts.account ? `Account ${opts.account} · ${rows.length} shown` : `${rows.length} shown`,
    sections: [
      {
        kind: "metrics",
        items: [
          { label: "Listed", value: rows.length },
          ...[...byStatus.entries()].map(([status, count]) => ({
            label: status,
            value: count,
            tone: status.toLowerCase() === "active" ? ("ok" as Tone) : undefined,
          })),
        ],
      },
      {
        kind: "table",
        title: "Records",
        columns: ["ICCID", "MSISDN", "Status", "Account", "Data used"],
        numeric: [4],
        empty: "No subscribers matched. Widen the filter or check the account scope.",
        rows: rows.map((r) => [
          r.iccid,
          r.msisdn ?? "—",
          r.status,
          r.account ?? "—",
          r.dataUsedBytes === undefined ? "—" : humanBytes(r.dataUsedBytes),
        ]),
      },
    ],
    actions: [
      { label: "Diagnose one", command: "diagnose_subscriber" },
      { label: "Usage detail", command: "subscriber_usage" },
    ],
  };
}

// ── Usage ────────────────────────────────────────────────────────────────────

export interface UsageInput {
  subject: string;
  timeline: Array<{ date: string; bytes: number }>;
  countries?: Array<{ country: string; bytes: number }>;
  totalBytes?: number;
}

export function usageScreen(input: UsageInput): Screen {
  const total = input.totalBytes ?? input.timeline.reduce((sum, p) => sum + p.bytes, 0);
  const peak = input.timeline.reduce(
    (best, p) => (p.bytes > best.bytes ? p : best),
    { date: "—", bytes: 0 },
  );

  const sections: Section[] = [
    {
      kind: "metrics",
      items: [
        { label: "Total", value: humanBytes(total) },
        { label: "Days", value: input.timeline.length },
        { label: "Peak day", value: humanBytes(peak.bytes), hint: peak.date },
        {
          label: "Daily average",
          value: humanBytes(input.timeline.length ? total / input.timeline.length : 0),
        },
      ],
    },
    {
      kind: "bars",
      title: "Daily usage",
      empty: "No usage recorded in this window.",
      items: input.timeline.map((p) => ({
        label: p.date,
        value: p.bytes,
        hint: humanBytes(p.bytes),
      })),
    },
  ];

  if (input.countries?.length) {
    sections.push({
      kind: "bars",
      title: "By country",
      items: input.countries
        .slice(0, 10)
        .map((c) => ({ label: c.country, value: c.bytes, hint: humanBytes(c.bytes) })),
    });
  }

  return {
    id: "usage",
    title: "Usage",
    subtitle: input.subject,
    sections,
    actions: [{ label: "Project depletion", command: "usage_projection" }],
  };
}

// ── Packages ─────────────────────────────────────────────────────────────────

export interface PackageRow {
  name: string;
  id: string | number;
  dataLimitBytes?: number;
  validityDays?: number;
  price?: number;
  recurring?: boolean;
}

export function packagesScreen(rows: PackageRow[]): Screen {
  return {
    id: "packages",
    title: "Package catalog",
    subtitle: `${rows.length} template(s)`,
    sections: [
      {
        kind: "metrics",
        items: [
          { label: "Templates", value: rows.length },
          { label: "Recurring", value: rows.filter((r) => r.recurring).length },
        ],
      },
      {
        kind: "table",
        title: "Templates",
        columns: ["Name", "ID", "Data", "Validity", "Price"],
        numeric: [2, 3, 4],
        empty: "No package templates visible to this account.",
        rows: rows.map((r) => [
          r.name,
          String(r.id),
          r.dataLimitBytes === undefined ? "—" : humanBytes(r.dataLimitBytes),
          r.validityDays === undefined ? "—" : `${r.validityDays}d`,
          r.price === undefined ? "—" : money(r.price),
        ]),
      },
    ],
    actions: [{ label: "Assign to a subscriber", command: "assign_package" }],
  };
}

// ── Billing ──────────────────────────────────────────────────────────────────

export interface BillingInput {
  currency?: string;
  balance?: number;
  pending?: number;
  events: Array<{ date: string; description: string; amount: number }>;
}

export function billingScreen(input: BillingInput): Screen {
  const spend = input.events.reduce((sum, e) => sum + (e.amount > 0 ? e.amount : 0), 0);
  return {
    id: "billing",
    title: "Billing",
    subtitle: `${input.events.length} recent event(s)`,
    sections: [
      {
        kind: "metrics",
        items: [
          {
            label: "Balance",
            value: input.balance === undefined ? "—" : money(input.balance, input.currency),
            tone: (input.balance ?? 0) < 10 ? "warn" : "ok",
          },
          {
            label: "Pending",
            value: input.pending === undefined ? "—" : money(input.pending, input.currency),
          },
          { label: "Recent spend", value: money(spend, input.currency) },
        ],
      },
      {
        kind: "table",
        title: "Recent events",
        columns: ["Date", "Description", "Amount"],
        numeric: [2],
        empty: "No billing events in this window.",
        rows: input.events.map((e) => [e.date, e.description, money(e.amount, input.currency)]),
      },
    ],
    actions: [{ label: "Check payouts", command: "stripe_connect_payouts" }],
  };
}

// ── Wallet ───────────────────────────────────────────────────────────────────

export interface WalletInput {
  balance: number;
  currency?: string;
  autoTopupEnabled?: boolean;
  threshold?: number;
  credits?: number;
}

export function walletScreen(input: WalletInput): Screen {
  const low = input.balance < (input.threshold ?? 10);
  return {
    id: "wallet",
    title: "Wallet",
    subtitle: input.autoTopupEnabled ? "Auto top-up is on" : "Auto top-up is off",
    sections: [
      {
        kind: "metrics",
        items: [
          {
            label: "Balance",
            value: money(input.balance, input.currency),
            tone: low ? "warn" : "ok",
          },
          ...(input.credits === undefined
            ? []
            : [{ label: "Credits", value: input.credits }]),
        ],
      },
      {
        kind: "keyvalue",
        title: "Settings",
        items: [
          { label: "Auto top-up", value: input.autoTopupEnabled ? "enabled" : "disabled" },
          {
            label: "Threshold",
            value: input.threshold === undefined ? "—" : money(input.threshold, input.currency),
          },
        ],
      },
      ...(low
        ? ([
            {
              kind: "note",
              tone: "warn",
              text: "Balance is under the top-up threshold. Package assignment fails at zero on package-only accounts.",
            },
          ] as Section[])
        : []),
    ],
    actions: [
      { label: "Top up", command: "wallet_topup_checkout" },
      { label: "Configure auto top-up", command: "wallet_auto_topup" },
    ],
  };
}

// ── Greenzone ────────────────────────────────────────────────────────────────

export function greenzoneScreen(entries: Array<{ value: string; note?: string }>): Screen {
  return {
    id: "greenzone",
    title: "Greenzone whitelist",
    subtitle: `${entries.length} entr${entries.length === 1 ? "y" : "ies"}`,
    sections: [
      { kind: "metrics", items: [{ label: "Entries", value: entries.length }] },
      {
        kind: "table",
        title: "Whitelisted",
        columns: ["Value", "Note"],
        empty: "Whitelist is empty — every destination follows the default policy.",
        rows: entries.map((e) => [e.value, e.note ?? "—"]),
      },
    ],
    actions: [
      { label: "Add an entry", command: "greenzone_whitelist_add" },
      { label: "Remove an entry", command: "greenzone_whitelist_remove" },
    ],
  };
}

// ── Storefront ───────────────────────────────────────────────────────────────

export interface StorefrontInput {
  brand: string;
  url?: string;
  target?: string;
  verified?: boolean | null;
  diagnosis?: string;
  probes?: Array<{ path: string; status: number; ok: boolean }>;
  secretsStaged?: string[];
  secretsMissing?: string[];
  plans?: number;
}

export function storefrontScreen(input: StorefrontInput): Screen {
  const sections: Section[] = [
    {
      kind: "metrics",
      items: [
        {
          label: "Status",
          value: input.verified === true ? "serving" : input.verified === false ? "broken" : "unknown",
          tone: input.verified === true ? "ok" : input.verified === false ? "critical" : "muted",
        },
        { label: "Host", value: input.target ?? "—" },
        { label: "Plans", value: input.plans ?? "—" },
        { label: "Secrets", value: input.secretsStaged?.length ?? 0 },
      ],
    },
    {
      kind: "keyvalue",
      title: "Deployment",
      items: [
        { label: "Brand", value: input.brand },
        { label: "URL", value: input.url ?? "not deployed" },
        ...(input.diagnosis ? [{ label: "Diagnosis", value: input.diagnosis, tone: "warn" as Tone }] : []),
      ],
    },
  ];

  if (input.probes?.length) {
    sections.push({
      kind: "table",
      title: "Probes",
      columns: ["Path", "Status"],
      numeric: [1],
      rows: input.probes.map((p) => [p.path, p.status === 0 ? "unreachable" : p.status]),
    });
  }
  if (input.secretsMissing?.length) {
    sections.push({
      kind: "note",
      tone: "critical",
      text: `Missing runtime secret(s): ${input.secretsMissing.join(", ")}. The build and deploy both succeed without them and the site fails at request time.`,
    });
  }

  return {
    id: "storefront",
    title: "Storefront",
    subtitle: input.url ?? input.brand,
    sections,
    actions: [
      { label: "Deploy", command: "carrier site deploy" },
      { label: "Check hosts", command: "carrier site targets" },
    ],
  };
}
